import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import Countdown from "./Countdown";
import { WEDDING } from "@/data/wedding";
import { scrollToId } from "@/lib/lenis";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.16, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export default function Hero() {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 800], [0, 72]);

  return (
    <section id="accueil" className="relative overflow-hidden pt-32 lg:pt-36">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-28 -top-28 h-[420px] w-[420px] rounded-full bg-sage/15 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-32 top-1/3 h-80 w-80 rounded-full bg-terracotta/10 blur-3xl"
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 sm:px-8 lg:grid-cols-12 lg:gap-10">
        <div className="lg:col-span-7">
          <motion.p
            {...fade(0.1)}
            className="font-mono text-[11px] uppercase tracking-[0.4em] text-fog"
          >
            Le mariage de
          </motion.p>

          <h1 className="mt-6 font-serif text-6xl leading-[0.92] text-ink sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
            <span className="block overflow-hidden pb-1">
              <motion.span
                custom={0}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                {WEDDING.bride}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                custom={1}
                variants={line}
                initial="hidden"
                animate="show"
                className="block"
              >
                <em className="not-italic italic text-terracotta">&amp;</em> {WEDDING.groom}
              </motion.span>
            </span>
          </h1>

          <motion.div {...fade(0.95)} className="mt-8 flex items-center gap-4">
            <span className="h-px w-12 bg-terracotta" />
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-taupe sm:text-sm">
              {WEDDING.dateLong}
            </p>
          </motion.div>

          <motion.p {...fade(1.05)} className="mt-3 flex items-center gap-2 text-taupe">
            <MapPin className="h-4 w-4 text-sage" />
            {WEDDING.city} — France
          </motion.p>

          <motion.div {...fade(1.2)} className="mt-10">
            <Countdown />
          </motion.div>

          <motion.div {...fade(1.35)} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              type="button"
              data-testid="hero-rsvp-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#rsvp");
              }}
              className="rounded-full bg-ink px-8 py-4 text-sm font-medium tracking-wide text-sand transition-all duration-300 hover:-translate-y-0.5 hover:bg-terracotta hover:shadow-[0_14px_30px_rgba(200,125,101,0.35)]"
            >
              Confirmer ma présence
            </button>
            <button
              type="button"
              data-testid="hero-program-button"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("#informations");
              }}
              className="rounded-full border border-ink/20 px-8 py-4 text-sm font-medium tracking-wide text-ink transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
            >
              Découvrir le programme
            </button>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ y: reduce ? 0 : parallaxY }} className="relative">
            <motion.div
              {...fade(0.5)}
              className="relative overflow-hidden rounded-b-3xl rounded-t-[220px] border border-line shadow-[0_30px_60px_rgba(44,38,35,0.12)]"
            >
              <img
                src="/images/couple.jpg"
                alt="Morgane et Bruno, enlacés au bord de la mer"
                className="aspect-[4/5] w-full object-cover object-[center_28%] sm:aspect-auto sm:h-[540px] lg:h-[600px]"
                loading="eager"
              />
              <div className="absolute inset-0 bg-terracotta/5" />
            </motion.div>

            <motion.div
              {...fade(1.5)}
              className="absolute -left-3 bottom-8 rounded-2xl border border-line bg-sand/90 px-5 py-4 shadow-[0_10px_30px_rgba(44,38,35,0.08)] backdrop-blur-md sm:-left-8"
              data-testid="hero-date-badge"
            >
              <p className="font-mono text-xs tracking-[0.3em] text-terracotta">
                {WEDDING.dateShort}
              </p>
              <p className="mt-1 font-serif text-xl font-semibold text-ink">
                {WEDDING.domaine.name}
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          scrollToId("#informations");
        }}
        aria-label="Défiler vers le programme"
        data-testid="hero-scroll-cue"
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-fog transition-colors hover:text-terracotta lg:flex"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.35em]">Défiler</span>
        <ArrowDown className="h-4 w-4" />
      </motion.button>
    </section>
  );
}
